/**
 * MyElsewhere theme JS. One small dependency-free module; everything here is
 * progressive enhancement — browsing, product forms, cart and search all work
 * without it via native form submissions.
 */
(() => {
  'use strict';

  /* ---------- drawers (menu, cart): one authoritative controller ----------
   * State is the DOM itself: at most one `dialog.drawer[open]` may exist —
   * there are no parallel open/closed booleans to drift. Managed drawers are
   * modal and mutually exclusive. All handlers are delegated at the document,
   * so cart-drawer innerHTML re-renders can never orphan a close button.
   * (The Elli panel is a separate non-modal surface outside this set.)
   */
  const openManagedDrawer = () => document.querySelector('dialog.drawer[open]');
  let drawerTrigger = null; // where focus returns when the drawer closes

  const syncDrawerTriggers = (dialog, expanded) => {
    document
      .querySelectorAll(`[data-open-dialog="${dialog.id}"]`)
      .forEach((el) => el.setAttribute('aria-expanded', String(expanded)));
  };

  // Deterministic close path. State sync must NOT depend on the dialog
  // `close` event: at least one embedded Chromium (the in-app preview) never
  // dispatches it, so every close the theme initiates runs through here.
  const closeDrawer = (dialog, { restoreFocus = true } = {}) => {
    if (!(dialog instanceof HTMLDialogElement) || !dialog.open) return;
    dialog.close();
    syncDrawerTriggers(dialog, false);
    if (restoreFocus && drawerTrigger?.isConnected) drawerTrigger.focus();
    drawerTrigger = null;
  };

  const openDrawer = (dialog, trigger = null) => {
    if (!(dialog instanceof HTMLDialogElement) || dialog.open) return;
    // Mutual exclusion: the previous drawer fully closes before the next
    // opens; its focus hand-back is skipped because the new dialog takes it.
    const current = openManagedDrawer();
    if (current) closeDrawer(current, { restoreFocus: false });
    drawerTrigger = trigger;
    dialog.showModal();
    syncDrawerTriggers(dialog, true);
  };

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-open-dialog]');
    if (opener) {
      const dialog = document.getElementById(opener.dataset.openDialog);
      if (dialog instanceof HTMLDialogElement) {
        event.preventDefault();
        openDrawer(dialog, opener);
      }
      return;
    }
    if (event.target.closest('[data-close-dialog]')) {
      const dialog = event.target.closest('dialog');
      if (dialog?.classList.contains('drawer')) closeDrawer(dialog);
      else dialog?.close(); // non-drawer dialogs (Elli panel) manage themselves
      return;
    }
    // Click on the backdrop (the dialog element itself, outside its box)
    if (event.target instanceof HTMLDialogElement) {
      const rect = event.target.getBoundingClientRect();
      const inside =
        event.clientX >= rect.left &&
        event.clientX <= rect.right &&
        event.clientY >= rect.top &&
        event.clientY <= rect.bottom;
      if (!inside) {
        if (event.target.classList.contains('drawer')) closeDrawer(event.target);
        else event.target.close();
      }
    }
  });

  // Backstop only, for closes the controller didn't initiate (e.g. the
  // browser's native Esc on a modal). Idempotent: aria re-sync is harmless
  // and focus restore is skipped when closeDrawer already handled it.
  // (`close` doesn't bubble, hence document-level capture.)
  document.addEventListener(
    'close',
    (event) => {
      const dialog = event.target;
      if (!(dialog instanceof HTMLDialogElement) || !dialog.classList.contains('drawer')) return;
      syncDrawerTriggers(dialog, false);
      if (drawerTrigger?.isConnected) drawerTrigger.focus();
      drawerTrigger = null;
    },
    true,
  );

  // Explicit Escape support alongside the browser's native modal handling.
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closeDrawer(openManagedDrawer());
  });

  /* ---------- AJAX cart ---------- */
  const CART_DRAWER_SECTION = 'cart-drawer';

  const refreshCartUI = async (shouldOpen) => {
    const res = await fetch(`${window.location.pathname}?sections=${CART_DRAWER_SECTION}`);
    if (!res.ok) return;
    const sections = await res.json();
    const html = sections[CART_DRAWER_SECTION];
    if (!html) return;

    const current = document.getElementById('cart-drawer');
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const next = parsed.getElementById('cart-drawer');
    if (current && next) {
      current.innerHTML = next.innerHTML;
      // Header count lives outside the drawer; read it from the fresh markup.
      const countText = next.querySelector('.drawer__header .eyebrow')?.textContent ?? '';
      const count = parseInt(countText.match(/\((\d+)\)/)?.[1] ?? '0', 10);
      document.querySelectorAll('[data-cart-count]').forEach((el) => {
        el.textContent = String(count);
        el.hidden = count === 0;
      });
      // Route through the controller so the menu (if open) closes first.
      if (shouldOpen) openDrawer(current);
    }
    evaluateGiftTier(); // fire-and-forget: analytics only, never blocks the UI
  };

  /* ---------- gift tier (analytics signal only) ----------
   * The truth about bundle gifts lives in the ORDER's real line items and is
   * applied at fulfilment (docs/adr/ADR-007). This only mirrors that rule for
   * the funnel: normal = cart holds The Gargoyle Trio; premium = Trio plus
   * any piece whose product type is Planter. Emits once per state change.
   */
  const GIFT_BUNDLE_HANDLE = 'the-gargoyle-trio';
  const evaluateGiftTier = async () => {
    try {
      const res = await fetch(`${window.Shopify?.routes?.root ?? '/'}cart.js`);
      if (!res.ok) return;
      const cart = await res.json();
      const hasTrio = cart.items.some((item) => item.handle === GIFT_BUNDLE_HANDLE);
      const hasPlanter = cart.items.some((item) => item.product_type === 'Planter');
      const tier = hasTrio ? (hasPlanter ? 'premium' : 'normal') : 'none';
      let previous = null;
      try {
        previous = sessionStorage.getItem('mea:gift_tier');
      } catch {
        /* storage unavailable — emit every evaluation rather than never */
      }
      if (tier !== previous) {
        try {
          sessionStorage.setItem('mea:gift_tier', tier);
        } catch {
          /* ignore */
        }
        if (tier !== 'none') window.MEA?.track('gift_tier_qualified', { tier });
      }
    } catch {
      /* analytics must never break the cart */
    }
  };

  // Intercept product forms marked for AJAX add-to-cart.
  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('form[data-ajax-cart]');
    if (!form) return;
    event.preventDefault();
    const submitButton = form.querySelector('[type="submit"][name="add"]');
    submitButton?.setAttribute('disabled', '');
    try {
      const res = await fetch(`${window.Shopify?.routes?.root ?? '/'}cart/add.js`, {
        method: 'POST',
        body: new FormData(form),
      });
      if (!res.ok) throw new Error(`add.js ${res.status}`);
      window.MEA?.track('add_to_cart', { source: form.dataset.source || 'product' });
      await refreshCartUI(true);
    } catch {
      // Fall back to the native flow so the shopper is never stuck.
      form.removeAttribute('data-ajax-cart');
      form.submit();
    } finally {
      submitButton?.removeAttribute('disabled');
    }
  });

  // Quantity +/− and remove buttons inside the cart drawer.
  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-cart-change]');
    if (!button) return;
    button.disabled = true;
    try {
      const res = await fetch(`${window.Shopify?.routes?.root ?? '/'}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          line: Number(button.dataset.line),
          quantity: Number(button.dataset.quantity),
        }),
      });
      if (res.ok) await refreshCartUI(false);
    } finally {
      button.disabled = false;
    }
  });

  /* ---------- bulk add (world transformation kits) ---------- */
  // Buttons ship hidden; only a JS-capable browser reveals them.
  document.querySelectorAll('[data-bulk-add]').forEach((btn) => {
    btn.hidden = false;
  });
  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-bulk-add]');
    if (!button) return;
    let items;
    try {
      items = JSON.parse(button.dataset.bulkAdd);
    } catch {
      return;
    }
    if (!Array.isArray(items) || items.length === 0) return;
    button.disabled = true;
    try {
      const res = await fetch(`${window.Shopify?.routes?.root ?? '/'}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items }),
      });
      if (res.ok) {
        const source = button.dataset.bulkSource || 'world-kit';
        window.MEA?.track('add_to_cart', { source, items: items.length });
        if (source === 'bundle') {
          window.MEA?.track('bundle_add_to_cart', {
            handle: button.dataset.bulkHandle || null,
            value: Number(button.dataset.bulkValue || 0) / 100,
          });
        }
        await refreshCartUI(true);
      }
    } finally {
      button.disabled = false;
    }
  });

  /* ---------- corner builder (world bundle/kit engine) ----------
   * Progressive enhancement over a plain linked product list: JS reveals the
   * checkboxes and summary, then adds the selected REAL variants via
   * /cart/add.js with a `_corner` line property for attribution. The total
   * is recomputed from the same live per-variant prices the rows carry.
   */
  document.querySelectorAll('[data-corner-builder]').forEach((builder) => {
    const items = [...builder.querySelectorAll('[data-corner-item]')];
    if (items.length === 0) return;
    const summary = builder.querySelector('[data-corner-summary]');
    const totalEl = builder.querySelector('[data-corner-total]');
    const addBtn = builder.querySelector('[data-corner-add]');
    if (!summary || !totalEl || !addBtn) return;

    const currency = builder.dataset.currency || 'AUD';
    const money = (cents) =>
      new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(cents / 100);

    items.forEach((box) => {
      box.hidden = false;
    });
    summary.hidden = false;

    const selection = () => items.filter((box) => box.checked && !box.disabled);
    const update = () => {
      const picked = selection();
      const total = picked.reduce((sum, box) => sum + Number(box.dataset.price || 0), 0);
      totalEl.textContent = picked.length ? money(total) : '—';
      addBtn.disabled = picked.length === 0;
    };
    items.forEach((box) => box.addEventListener('change', update));
    update();

    addBtn.addEventListener('click', async () => {
      const picked = selection();
      if (picked.length === 0) return;
      addBtn.disabled = true;
      try {
        const res = await fetch(`${window.Shopify?.routes?.root ?? '/'}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: picked.map((box) => ({
              id: Number(box.value),
              quantity: 1,
              properties: { _corner: 'true' },
            })),
          }),
        });
        if (res.ok) {
          const value = picked.reduce((sum, box) => sum + Number(box.dataset.price || 0), 0) / 100;
          window.MEA?.track('corner_add_to_cart', { items: picked.length, value });
          picked.forEach((box) => {
            box.checked = false;
          });
          await refreshCartUI(true);
        }
      } finally {
        update();
      }
    });
  });

  // Bundle impressions: any element carrying data-bundle-view (bundle PDP
  // block, corner spotlight) reports one view per page load.
  document.querySelectorAll('[data-bundle-view]').forEach((el) => {
    window.MEA?.track('bundle_view', { handle: el.dataset.bundleView });
  });

  /* ---------- predictive search ---------- */
  const searchInput = document.querySelector('[data-predictive-search-input]');
  const searchResults = document.querySelector('[data-predictive-search-results]');
  if (searchInput && searchResults) {
    let timer;
    searchInput.addEventListener('input', () => {
      clearTimeout(timer);
      const query = searchInput.value.trim();
      if (query.length < 2) {
        searchResults.hidden = true;
        return;
      }
      timer = setTimeout(async () => {
        const url = `${window.Shopify?.routes?.root ?? '/'}search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=6`;
        const res = await fetch(url);
        if (!res.ok) return;
        const data = await res.json();
        const products = data?.resources?.results?.products ?? [];
        // Build via DOM APIs (not innerHTML) so response text is never parsed as HTML.
        searchResults.textContent = '';
        if (products.length) {
          const list = document.createElement('ul');
          for (const p of products) {
            const item = document.createElement('li');
            const link = document.createElement('a');
            link.href = p.url;
            const title = document.createElement('span');
            title.textContent = p.title;
            const price = document.createElement('span');
            price.textContent = p.price ?? '';
            link.append(title, price);
            item.append(link);
            list.append(item);
          }
          searchResults.append(list);
        }
        searchResults.hidden = products.length === 0;
      }, 250);
    });
  }

  /* ---------- small helpers ---------- */
  // Sort selects submit their surrounding GET form on change.
  document.addEventListener('change', (event) => {
    const select = event.target.closest('[data-submit-on-change]');
    if (select) select.closest('form')?.submit();
  });

  // Funnel: checkout starts (cart page + drawer).
  document.addEventListener('click', (event) => {
    if (event.target.closest('button[name="checkout"], input[name="checkout"]')) {
      window.MEA?.track('begin_checkout', {});
    }
  });

  // Deliberate public surface for other modules (Elli) to refresh the cart UI.
  window.MECart = { refresh: refreshCartUI };
})();
